import React, { useState } from "react";
import { Card, Form, Space, Typography, Button, App } from "antd";
import type { UploadFile } from "antd/es/upload";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import { FinanceTransactionForm } from "../components/FinanceTransactionForm";
import { useUploadFileMutation, useDeleteMediaMutation } from "../graphql/generated/hooks";
import { useFinanceTreeItems } from "../hooks/useFinanceTreeItems";
import { useCreateFinanceTransaction } from "../hooks/useCreateFinanceTransaction";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { TransactionLine } from "../types/financeTransaction.types";

const { Text } = Typography;

function FinanceTransactionCreate() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message: messageApi } = App.useApp();

  // State
  const [transactionLines, setTransactionLines] = useState<TransactionLine[]>([
    { key: "1" },
    { key: "2" },
  ]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedMediaData, setUploadedMediaData] = useState<any>(null); // Single object
  const [saving, setSaving] = useState(false);

  // Fetch finance tree items
  const { data: financeTreeData, loading: financeTreeLoading } = useFinanceTreeItems();
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();
  const [deleteMedia, { loading: deletingMedia }] = useDeleteMediaMutation();
  const [createFinanceTransaction, { loading: creating }] = useCreateFinanceTransaction();

  // Get finance tree items from response
  const financeTreeItems = financeTreeData?.getAllFinanceTreeItems || [];

  // Calculate level based on serialNumber (count dots)
  const calculateLevel = (serialNumber: string): number => {
    return (serialNumber.match(/\./g) || []).length;
  };

  // Create indented options for the select dropdown
  const financeTreeOptions = financeTreeItems.map((item) => {
    const level = calculateLevel(item.serialNumber);
    return {
      label: `${"  ".repeat(level)}${item.serialNumber} - ${currentLang === "ar" ? item.nameAr : item.name}`,
      value: item.id,
      level: level,
    };
  });

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  // Real upload function using GraphQL (from PartnerCategoryList pattern)
  const handleCustomUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    
    try {
      onProgress({ percent: 0 });
      
      // Convert file to base64
      const base64String = await convertFileToBase64(file);
      onProgress({ percent: 50 });
      
      // Upload using GraphQL mutation
      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: file.name,
          folder: 'finance-transactions',
        },
      });
      
      onProgress({ percent: 100 });
      
      if (result.data?.uploadFile) {
        const mediaData = {
          id: result.data.uploadFile.id,
          fileName: result.data.uploadFile.fileName,
          originalName: result.data.uploadFile.originalName,
          mimeType: result.data.uploadFile.mimeType,
          size: result.data.uploadFile.size,
          type: 'DOCUMENT',
          url: result.data.uploadFile.url,
          altText: result.data.uploadFile.altText,
          description: result.data.uploadFile.description,
        };
        
        // Replace with new media (last upload wins)
        setUploadedMediaData(mediaData);
        
        onSuccess({
          id: result.data.uploadFile.id,
          url: result.data.uploadFile.url,
          mediaData: mediaData, // Store full media data for future use
        }, file);
      } else {
        onError(new Error('Upload failed: No data returned'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      onError(error);
    }
  };

  const handleRemove = async (file: any) => {
    try {
      // If the file has been uploaded and has an ID, delete it from the server
      if (file.response?.id) {
        await deleteMedia({
          variables: {
            id: file.response.id,
          },
        });
        
        // Clear uploaded media data
        setUploadedMediaData(null);
        
        messageApi.success(`${file.name} deleted successfully`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      messageApi.error(`Failed to delete ${file.name}`);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    // Validate that we have at least one line with data
    const validLines = transactionLines.filter(
      (line) => line.financeItemId && (line.debit || line.credit)
    );

    if (validLines.length === 0) {
      messageApi.error(t("error.transaction.no_valid_lines"));
      return;
    }

    try {
      setSaving(true);

      // Format transaction lines for the mutation
      const transactionLinesInput = validLines.map((line) => ({
        financeTreeId: line.financeItemId!,
        description: line.description || "",
        debit: line.debit || undefined,
        credit: line.credit || undefined,
      }));

      // Format media data if exists (single object)
      const mediaInput = uploadedMediaData ? {
        fileName: uploadedMediaData.fileName,
        originalName: uploadedMediaData.originalName,
        mimeType: uploadedMediaData.mimeType,
        size: uploadedMediaData.size,
        type: uploadedMediaData.type,
        url: uploadedMediaData.url,
        isActive: true,
        altText: uploadedMediaData.altText || values.description || "Transaction attachment",
        description: uploadedMediaData.description || values.description || "",
      } : null;

      // Call GraphQL mutation to create the transaction
      const result = await createFinanceTransaction({
        variables: {
          createFinanceTreeTransactionDto: {
            date: values.date?.format("YYYY-MM-DD"),
            description: values.description || "",
            media: mediaInput,
            transactionLines: transactionLinesInput,
          },
        },
      });

      if (result.data?.createFinanceTreeTransaction) {
        messageApi.success(
          `${t("message.success.transaction_created")} - ${result.data.createFinanceTreeTransaction.transactionNumber}`
        );

        // Navigate back to list
        setTimeout(() => {
          navigate("/finance/transactions");
        }, 1500);
      }
    } catch (error: any) {
      console.error("Create transaction error:", error);
      messageApi.error(error.message || t("message.error.data_create_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/finance/transactions");
  };

  return (
    <RobinPage
      title={t("label.menu.finance_transaction")}
      actions={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/finance/transactions")}>
          {t("button.back")}
        </Button>
      }
    >
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        title={
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 16 }}>
              {t("button.add_transaction")}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("label.finance_transaction_description")}
            </Text>
          </Space>
        }
      >
        <FinanceTransactionForm
          form={form}
          transactionLines={transactionLines}
          setTransactionLines={setTransactionLines}
          financeTreeOptions={financeTreeOptions}
          financeTreeLoading={financeTreeLoading}
          fileList={fileList}
          setFileList={setFileList}
          handleCustomUpload={handleCustomUpload}
          handleRemove={handleRemove}
          uploading={uploading}
          isViewMode={false}
          isEditMode={false}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          saving={saving || creating}
          messageApi={messageApi}
        />
      </Card>
    </RobinPage>
  );
}

export default FinanceTransactionCreate;
